import { call, put, takeEvery, all, fork } from "redux-saga/effects";
import { toast } from "react-toastify";

import {
  MASTER_SIM_LIST,
  MASTER_SIM_DATA,
  MASTER_SIM_COMPANIES,
  MASTER_SIM_CREATE,
  MASTER_SIM_UPDATE,
  MASTER_SIM_DELETE,
  MASTER_SIM_SYNC,
  MASTER_SIM_REGISTER,
  MASTER_SIM_ACTIVATE,
  MASTER_SIM_SUSPEND,
  MASTER_SIM_ASSIGN,
  MASTER_SIM_UNASSIGN,
} from "./actionType";

import {
  masterSimList,
  masterSimListSuccess,
  masterSimListFailure,
  masterSimDataSuccess,
  masterSimDataFailure,
  masterSimCompaniesSuccess,
  masterSimCompaniesFailure,
  masterSimCreateSuccess,
  masterSimCreateFailure,
  masterSimUpdateSuccess,
  masterSimUpdateFailure,
  masterSimDeleteSuccess,
  masterSimDeleteFailure,
  masterSimSyncSuccess,
  masterSimSyncFailure,
  masterSimRegisterSuccess,
  masterSimRegisterFailure,
  masterSimActivateSuccess,
  masterSimActivateFailure,
  masterSimSuspendSuccess,
  masterSimSuspendFailure,
  masterSimAssignSuccess,
  masterSimAssignFailure,
  masterSimUnassignSuccess,
  masterSimUnassignFailure,
} from "./action";

import {
  getMasterSimList,
  getMasterSimData,
  getMasterSimCompanies,
  createMasterSim,
  updateMasterSim,
  deleteMasterSim,
  syncMasterSim,
  registerMasterSim,
  activateMasterSim,
  suspendMasterSim,
  assignMasterSim,
  unassignMasterSim,
} from "../../helpers/fakebackend_helper";

// Our API returns HTTP 200 even for logical failures, with the real outcome in
// the body { status, message }. Toast off that so the user always gets feedback.
const notify = (response, successMsg) => {
  const ok = response && Number(response.status) === 200;
  if (ok) {
    toast.success(successMsg || response.message || "Success");
  } else {
    toast.error((response && response.message) || "Something went wrong");
  }
  return ok;
};

// LIST
function* fetchMasterSimList() {
  try {
    const response = yield call(getMasterSimList);
    yield put(masterSimListSuccess(response));
  } catch (error) {
    yield put(masterSimListFailure(error));
  }
}

// DATA
function* fetchMasterSimData(action) {
  try {
    const response = yield call(getMasterSimData, action.payload);
    yield put(masterSimDataSuccess(response));
  } catch (error) {
    yield put(masterSimDataFailure(error));
  }
}

// COMPANIES
function* fetchMasterSimCompanies() {
  try {
    const response = yield call(getMasterSimCompanies);
    yield put(masterSimCompaniesSuccess(response));
  } catch (error) {
    yield put(masterSimCompaniesFailure(error));
  }
}

// CREATE
function* createMasterSimSaga(action) {
  try {
    const response = yield call(createMasterSim, action.payload);
    notify(response, "SIM created.");
    yield put(masterSimCreateSuccess(response));
  } catch (error) {
    toast.error("Failed to create SIM.");
    yield put(masterSimCreateFailure(error));
  }
}

// UPDATE
function* updateMasterSimSaga(action) {
  try {
    const response = yield call(updateMasterSim, action.payload);
    notify(response, "SIM updated.");
    yield put(masterSimUpdateSuccess(response));
  } catch (error) {
    toast.error("Failed to update SIM.");
    yield put(masterSimUpdateFailure(error));
  }
}

// DELETE
function* deleteMasterSimSaga(action) {
  try {
    const response = yield call(deleteMasterSim, action.payload);
    notify(response, "SIM deleted.");
    yield put(masterSimDeleteSuccess(response));
  } catch (error) {
    toast.error("Failed to delete SIM.");
    yield put(masterSimDeleteFailure(error));
  }
}

// SYNC
function* syncMasterSimSaga() {
  try {
    const response = yield call(syncMasterSim);
    const created = response?.created ?? 0;
    const updated = response?.updated ?? 0;
    notify(response, `emnify sync complete — ${created} added, ${updated} updated.`);
    yield put(masterSimSyncSuccess(response));
    // Refresh the grid exactly when the (possibly slow, paged) sync finishes,
    // rather than on a fixed timeout in the component.
    if (response && Number(response.status) === 200) {
      yield put(masterSimList());
    }
  } catch (error) {
    toast.error("emnify sync failed.");
    yield put(masterSimSyncFailure(error));
  }
}

// REGISTER (Change #100343): registers the batch at emnify, and the backend
// syncs straight after, so refresh the grid when it comes back OK.
function* registerMasterSimSaga(action) {
  try {
    const response = yield call(registerMasterSim, action.payload);
    notify(response);
    yield put(masterSimRegisterSuccess(response));
    if (response && Number(response.status) === 200) {
      yield put(masterSimList());
    }
  } catch (error) {
    toast.error("emnify registration failed.");
    yield put(masterSimRegisterFailure(error));
  }
}

// ACTIVATE
function* activateMasterSimSaga(action) {
  try {
    const response = yield call(activateMasterSim, action.payload);
    notify(response, "SIM activated.");
    yield put(masterSimActivateSuccess(response));
  } catch (error) {
    toast.error("Failed to activate SIM.");
    yield put(masterSimActivateFailure(error));
  }
}

// SUSPEND
function* suspendMasterSimSaga(action) {
  try {
    const response = yield call(suspendMasterSim, action.payload);
    notify(response, "SIM suspended.");
    yield put(masterSimSuspendSuccess(response));
  } catch (error) {
    toast.error("Failed to suspend SIM.");
    yield put(masterSimSuspendFailure(error));
  }
}

// ASSIGN
function* assignMasterSimSaga(action) {
  try {
    const response = yield call(assignMasterSim, action.payload);
    notify(response, "SIM assigned.");
    yield put(masterSimAssignSuccess(response));
  } catch (error) {
    toast.error("Failed to assign SIM.");
    yield put(masterSimAssignFailure(error));
  }
}

// UNASSIGN
function* unassignMasterSimSaga(action) {
  try {
    const response = yield call(unassignMasterSim, action.payload);
    notify(response, "SIM returned to stock.");
    yield put(masterSimUnassignSuccess(response));
  } catch (error) {
    toast.error("Failed to return SIM to stock.");
    yield put(masterSimUnassignFailure(error));
  }
}

export default function* masterSimSaga() {
  yield all([
    fork(function* () {
      yield takeEvery(MASTER_SIM_LIST, fetchMasterSimList);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_DATA, fetchMasterSimData);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_COMPANIES, fetchMasterSimCompanies);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_CREATE, createMasterSimSaga);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_UPDATE, updateMasterSimSaga);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_DELETE, deleteMasterSimSaga);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_SYNC, syncMasterSimSaga);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_REGISTER, registerMasterSimSaga);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_ACTIVATE, activateMasterSimSaga);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_SUSPEND, suspendMasterSimSaga);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_ASSIGN, assignMasterSimSaga);
    }),
    fork(function* () {
      yield takeEvery(MASTER_SIM_UNASSIGN, unassignMasterSimSaga);
    }),
  ]);
}
